import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MaterialModule } from 'src/app/material.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IClinicalSubCategory } from 'src/app/pages/category/clicnical-sub-category/iclinical-sub-category';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { OPDDetails, OPDRecord } from '../../iopddetails';
import { OPDService } from '../../../opd.service';
import { ToastrService } from 'ngx-toastr';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MatDialog } from '@angular/material/dialog';
import { ClinicalitemDetailComponent } from 'src/app/pages/category/clinical-item/detail/clinicalitem-detail.component';
import { IClinicalCategory } from 'src/app/pages/category/clicnical-category/iclinical-category';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { generateSuggestions } from 'src/app/common/function-helper';

const QUEUE_PROCESSING_DELAY_MS = 300;
const DEBOUNCE_DURATION_MS = 2000;
const RECENT_SAVE_EXPIRY_MS = 10000;
const MANUAL_SAVE_FLAG_DURATION_MS = 5000;

@Component({
  selector: 'app-opd-subcategory-panel',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslateModule, FormsModule, ReactiveFormsModule, TablerIconsModule],
  templateUrl: './subcategory-panel.component.html'
})
export class OpdSubcategoryPanelComponent implements OnChanges, OnDestroy {

  @Input() subcategory?: IClinicalSubCategory;
  @Input() category?: IClinicalCategory;
  @Input() items: OPDDetails[] = [];
  @Input() checkedRecords: OPDRecord[] = [];
  @Input() panelId?: string;
  @Input() opdId: string | null = null;
  @Input() expanded: boolean = false;

  @Output() panelOpen = new EventEmitter<string>();
  @Output() panelClose = new EventEmitter<string>();
  @Output() stateChanged = new EventEmitter<void>();

  listOfduration: { [key: number]: string[] } = {};
  private inputControls: { [key: number]: FormControl } = {};
  private saveSubjects: { [key: number]: Subject<{ data: any, item: OPDDetails }> } = {};
  private recentlySaved = new Set<string>();
  private savingNewItems: Set<number> = new Set();
  private destroy$ = new Subject<void>();
  private requestQueue: (() => Promise<void>)[] = [];
  private isProcessingQueue = false;

  constructor(
    private opdService: OPDService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private translationUtils: TranslationUtils
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.ensureControls();
    }

    if (changes['items'] || changes['checkedRecords']) {
      this.applyCheckedRecords();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    Object.values(this.saveSubjects).forEach(subject => subject.complete());
    this.saveSubjects = {};
    this.inputControls = {};
    this.listOfduration = {};
    this.recentlySaved.clear();
    this.savingNewItems.clear();
  }

  get hasItems(): boolean {
    return (this.items?.length ?? 0) > 0;
  }

  get checkedCount(): number {
    if (!this.items) {
      return 0;
    }
    return this.items.filter(item => item.isChecked).length;
  }

  onPanelOpen(): void {
    if (this.panelId) {
      this.panelOpen.emit(this.panelId);
    }
  }

  onPanelClose(): void {
    if (this.panelId) {
      this.panelClose.emit(this.panelId);
    }
  }

  getControl(item: OPDDetails): FormControl {
    if (!this.inputControls[item.id]) {
      this.createInputControl(item);
    }
    return this.inputControls[item.id];
  }

  getDurationOptions(item: OPDDetails): string[] {
    return this.listOfduration[item.id] || [];
  }

  onCheckboxChange(item: OPDDetails): void {
    if (item.isChecked) {
      this.handleItemCheck(item);
    } else {
      this.handleItemUncheck(item);
    }
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent, item: OPDDetails): void {
    const value = event.option.value;
    const ctrl = this.getControl(item);

    this.setManualSaveFlag(item, value);
    ctrl.setValue(value, { emitEvent: false });
    this.trySaveDuration(item, value);
    this.clearManualSaveFlag(item);
  }

  private ensureControls(): void {
    (this.items || []).forEach(item => {
      if (!this.inputControls[item.id]) {
        this.createInputControl(item);
      }
    });
  }

  private applyCheckedRecords(): void {
    if (!this.items) {
      return;
    }

    this.items.forEach(item => {
      const matched = this.checkedRecords?.find(record => record.clinicalItemId === item.id);
      const ctrl = this.getControl(item);

      if (matched) {
        Object.assign(item, {
          isChecked: true,
          duration: matched.duration,
          note: matched.note,
          recordId: matched.id
        });
        item.manualSaveTriggered = true;
        ctrl.setValue(matched.duration || '', { emitEvent: false });
        setTimeout(() => item.manualSaveTriggered = false, MANUAL_SAVE_FLAG_DURATION_MS);
      } else {
        this.resetItemState(item);
      }
    });
  }

  private resetItemState(item: OPDDetails): void {
    item.isChecked = false;
    item.duration = '';
    item.recordId = undefined;

    const ctrl = this.getControl(item);
    ctrl.reset();
  }

  private createInputControl(item: OPDDetails): void {
    const control = new FormControl('');
    this.inputControls[item.id] = control;
    this.setupDebouncedSave(item);
    this.setupValueChangeListener(item, control);
  }

  private setupDebouncedSave(item: OPDDetails): void {
    if (!this.saveSubjects[item.id]) {
      this.saveSubjects[item.id] = new Subject<{ data: any, item: OPDDetails }>();

      this.saveSubjects[item.id].pipe(
        debounceTime(DEBOUNCE_DURATION_MS),
        takeUntil(this.destroy$)
      ).subscribe(({ data, item: itm }) => {
        this.handleDebouncedSave(data, itm);
      });
    }
  }

  private setupValueChangeListener(item: OPDDetails, control: FormControl): void {
    control.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(value => {
      if (item.manualSaveTriggered || item.lastDropdownValue === value) {
        return;
      }

      this.updateDurationSuggestions(item, value);

      if (item.isChecked && item.isSince && value?.trim()) {
        this.saveSubjects[item.id].next({
          data: { duration: value, checked: item.isChecked },
          item
        });
      }
    });
  }

  private handleDebouncedSave(data: any, item: OPDDetails): void {
    if (item.isChecked && item.isSince && data?.duration) {
      this.trySaveDuration(item, data.duration);
    }
  }

  private handleItemCheck(item: OPDDetails): void {
    if (item.isSince) {
      return; // duration required before saving
    }

    const opdId = this.getNumericOpdId();
    if (!opdId) {
      return;
    }

    const payload = {
      opdId,
      clinicalItemId: item.id
    };

    this.enqueueRequest(() => this.saveItem(payload, item));
  }

  private handleItemUncheck(item: OPDDetails): void {
    if (item.recordId) {
      this.enqueueRequest(() => this.deleteItem(Number(item.recordId)));
    }
  }

  private trySaveDuration(item: OPDDetails, value: string): void {
    if (!value?.trim() || !item.isChecked) {
      return;
    }

    const saveKey = `${item.id}_${value.trim()}`;
    if (this.recentlySaved.has(saveKey)) {
      return;
    }

    this.recentlySaved.add(saveKey);
    setTimeout(() => this.recentlySaved.delete(saveKey), RECENT_SAVE_EXPIRY_MS);

    const opdId = this.getNumericOpdId();
    if (!opdId) {
      return;
    }

    const payload: any = {
      opdId,
      clinicalItemId: item.id,
      duration: value.trim()
    };

    if (item.recordId) {
      payload.id = item.recordId;
    }

    this.enqueueRequest(() => this.saveItem(payload, item));
  }

  private updateDurationSuggestions(item: OPDDetails, value: string): void {
    this.listOfduration[item.id] = generateSuggestions(value ?? '');
  }

  private setManualSaveFlag(item: OPDDetails, value: string): void {
    item.manualSaveTriggered = true;
    item.lastDropdownValue = value;
  }

  private clearManualSaveFlag(item: OPDDetails): void {
    setTimeout(() => {
      item.manualSaveTriggered = false;
      item.lastDropdownValue = undefined;
    }, MANUAL_SAVE_FLAG_DURATION_MS);
  }

  private saveItem(payload: any, sourceItem?: OPDDetails): Promise<void> {
    if (payload.id) {
      return this.updateExistingItem(payload);
    }

    return this.createNewItem(payload, sourceItem);
  }

  private updateExistingItem(item: any): Promise<void> {
    return this.opdService.updateOPDClinicalRecord(item).toPromise()
      .then(() => {
        // this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.UPDATE'));
        this.stateChanged.emit();
      })
      .catch(error => {
        this.toastr.error('Error updating record. Please try again.');
        throw error;
      });
  }

  private createNewItem(item: any, sourceItem?: OPDDetails): Promise<void> {
    const itemKey = item.clinicalItemId;

    if (this.savingNewItems.has(itemKey)) {
      return Promise.resolve();
    }

    this.savingNewItems.add(itemKey);

    return this.opdService.createOPDClinicalRecord(item).toPromise()
      .then((response) => {
        if (sourceItem && response?.result?.id) {
          sourceItem.recordId = response.result.id;
        }
        this.stateChanged.emit();
      })
      .catch(error => {
        this.toastr.error('Error saving record. Please try again.');
        throw error;
      })
      .finally(() => {
        this.savingNewItems.delete(itemKey);
      });
  }

  private deleteItem(recordId: number): Promise<void> {
    if (!recordId) {
      return Promise.resolve();
    }

    return this.opdService.deleteOPDClinicalRecord(recordId).toPromise()
      .then(() => {
        // this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE'));
        this.stateChanged.emit();
      })
      .catch(error => {
        this.toastr.error('Error deleting record. Please try again.');
        throw error;
      });
  }

  private enqueueRequest(requestFn: () => Promise<void>): void {
    this.requestQueue.push(requestFn);
    this.processQueue();
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      const requestFn = this.requestQueue.shift();
      if (requestFn) {
        try {
          await requestFn();
        } catch (err) {
          console.error('Request failed:', err);
        }
        await this.delay(QUEUE_PROCESSING_DELAY_MS);
      }
    }

    this.isProcessingQueue = false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private getNumericOpdId(): number | null {
    return this.opdId ? Number(this.opdId) : null;
  }

  addClinicalItem(event: MouseEvent) {
    event.stopPropagation();
    this.dialog.open(ClinicalitemDetailComponent, {
      autoFocus: false,
      width: '700px',
      height: 'auto',
      disableClose: true,
      data: {
        fromOPDcategoryvalue: this.category,
        fromOPDSubcategoryvalue: this.subcategory ?? null
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.stateChanged.emit();
      }
    });
  }

}
